/**
 * Удаление колонки и целого блока колонок — в два шага.
 *
 * Колонка в таблице общая: её удаление видят все семеро. Поэтому сначала
 * показываем, что именно исчезнет и что останется, и только потом просим
 * написать название — набрать слово случайно нельзя, а нажать «Да» можно.
 *
 * Физически поле архивируется: значения остаются в базе и в истории. Так и
 * написано в диалоге — обещать «безвозвратно» там, где это неправда, нельзя.
 */
import { useEffect, useState } from 'react'
import { Button, Input, Modal } from '../../../components/index.js'
import { api, asOneOperation } from '../../../lib/api.js'
import { useApp } from '../../../store/app-store.js'
import { tableTexts as t } from '../texts.table.js'
import './RemoveColumn.css'

export interface RemoveTarget {
  kind: 'column' | 'group'
  /** Название, которое человек видит и которое попросим написать. */
  title: string
  /** Поля, которые будут заархивированы. */
  fields: { id: number; name: string }[]
}

export interface RemoveColumnDialogProps {
  target: RemoveTarget
  onClose: () => void
  /** Удаление прошло: вызывающему обычно нужно закрыть своё меню. */
  onDone?: () => void
}

export function RemoveColumnDialog({ target, onClose, onDone }: RemoveColumnDialogProps) {
  const toast = useApp((state) => state.toast)
  const reloadBootstrap = useApp((state) => state.reloadBootstrap)

  const [step, setStep] = useState<1 | 2>(1)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => { setStep(1); setTyped('') }, [target.kind, target.title])

  const isGroup = target.kind === 'group'
  const matches = typed.trim().toLocaleLowerCase('ru') === target.title.trim().toLocaleLowerCase('ru')

  async function remove(): Promise<void> {
    if (!matches || busy) return
    setBusy(true)
    try {
      /* Блок уходит одним движением — и в истории он должен быть одной
         операцией, а не пятью отдельными архивациями. */
      await asOneOperation(async () => {
        for (const field of target.fields) await api.archiveField(field.id)
      })
      await reloadBootstrap()
      toast('info', isGroup ? t.removeColumn.doneGroup(target.title) : t.removeColumn.doneColumn(target.title))
      onDone?.()
      onClose()
    } catch (err) {
      toast('error', err instanceof Error ? err.message : t.removeColumn.failed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="s"
      dismissible={false}
      title={isGroup ? t.removeColumn.groupTitle(target.title) : t.removeColumn.columnTitle(target.title)}
      footer={step === 1 ? (
        <>
          <Button variant="secondary" onClick={onClose}>{t.removeColumn.cancel}</Button>
          <Button variant="danger-ghost" onClick={() => setStep(2)}>{t.removeColumn.next}</Button>
        </>
      ) : (
        <>
          <Button variant="secondary" onClick={onClose}>{t.removeColumn.cancel}</Button>
          <Button variant="danger-ghost" loading={busy} disabled={!matches} onClick={() => void remove()}>
            {isGroup ? t.removeColumn.finalGroup : t.removeColumn.finalColumn}
          </Button>
        </>
      )}
    >
      {step === 1 ? (
        <div className="remove-column">
          <p className="t-body">
            {isGroup ? t.removeColumn.whatGroup(target.fields.length) : t.removeColumn.whatColumn}
          </p>
          {isGroup ? (
            <ul className="remove-column__list">
              {target.fields.map((field) => <li key={field.id} className="t-body">{field.name}</li>)}
            </ul>
          ) : null}
          <p className="remove-column__note t-meta">{t.removeColumn.kept}</p>
          <p className="remove-column__note t-meta">{t.removeColumn.cannotUndo}</p>
        </div>
      ) : (
        <div className="remove-column">
          <p className="t-body">{t.removeColumn.confirmHint(target.title)}</p>
          <Input
            value={typed}
            size="m"
            autoFocus
            placeholder={t.removeColumn.confirmPlaceholder}
            aria-label={t.removeColumn.confirmPlaceholder}
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter' && matches) { event.preventDefault(); void remove() } }}
          />
          {typed.trim() !== '' && !matches ? (
            <p className="remove-column__mismatch t-meta">{t.removeColumn.mismatch}</p>
          ) : null}
        </div>
      )}
    </Modal>
  )
}
